import { Image as ImageIcon, Palette, Plus, Search, Type } from "lucide-react";
import { PRODUCTS } from "@/constants/media";
import { Eyebrow, Reveal } from "./Reveal";

function ScreenHeader({ title }) {
    return (
        <div className="flex items-center justify-between px-4 pb-3">
            <span className="text-[15px] font-light tracking-tight text-slate-950">{title}</span>
            <span className="h-6 w-6 rounded-full bg-slate-200" aria-hidden="true" />
        </div>
    );
}

function Chip({ active, children }) {
    return (
        <span
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[10px] font-light ${
                active ? "bg-slate-950 text-white" : "border border-slate-200 text-slate-500"
            }`}
        >
            {children}
        </span>
    );
}

function DiscoverScreen() {
    return (
        <div className="flex h-full flex-col pt-9" data-testid="mockup-screen-discover">
            <ScreenHeader title="Discover" />
            <div className="mx-4 mb-3 flex items-center gap-2 rounded-full bg-slate-100 px-3.5 py-2.5 text-[11px] font-extralight text-slate-400">
                <Search size={12} strokeWidth={1.5} />
                Search fashion, brands, moods…
            </div>
            <div className="flex gap-1.5 overflow-hidden px-4 pb-3">
                <Chip active>All</Chip>
                <Chip>New in</Chip>
                <Chip>Trending</Chip>
                <Chip>Edits</Chip>
            </div>
            <div className="grid grid-cols-2 gap-2.5 px-4">
                {PRODUCTS.slice(0, 4).map((p) => (
                    <div key={p.id}>
                        <img src={p.src} alt={p.name} loading="lazy" className="aspect-[3/4] w-full rounded-2xl object-cover" />
                        <div className="flex items-center justify-between px-0.5 pt-1.5">
                            <span className="text-[10px] font-light text-slate-600">{p.name}</span>
                            <span className="text-[10px] font-light text-slate-400">{p.price}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ShopScreen() {
    return (
        <div className="flex h-full flex-col pt-9" data-testid="mockup-screen-shop">
            <ScreenHeader title="Shop" />
            <div className="flex gap-1.5 px-4 pb-3">
                <Chip>Under £100</Chip>
                <Chip active>£100–£250</Chip>
                <Chip>£250+</Chip>
            </div>
            <div className="flex flex-col gap-2.5 px-4">
                {PRODUCTS.slice(4, 7).map((p) => (
                    <div
                        key={p.id}
                        className="flex items-center gap-3 rounded-2xl border border-slate-100 p-2"
                    >
                        <img src={p.src} alt={p.name} loading="lazy" className="h-16 w-14 rounded-xl object-cover" />
                        <div className="flex flex-col gap-1">
                            <span className="text-[11px] font-light text-slate-950">{p.name}</span>
                            <span className="text-[9px] font-extralight text-slate-400">Multiple retailers</span>
                        </div>
                        <span className="ml-auto text-[11px] font-light text-slate-500">{p.price}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StudioScreen() {
    return (
        <div className="flex h-full flex-col pt-9" data-testid="mockup-screen-studio">
            <ScreenHeader title="Studio" />
            <div className="relative mx-4 flex-1 overflow-hidden rounded-2xl bg-[#F8F9FA]">
                <img
                    src={PRODUCTS[0].src}
                    alt="Moodboard piece"
                    loading="lazy"
                    className="absolute left-4 top-6 w-24 rotate-[-6deg] rounded-xl object-cover shadow-lg aspect-[3/4]"
                />
                <img
                    src={PRODUCTS[2].src}
                    alt="Moodboard piece"
                    loading="lazy"
                    className="absolute right-4 top-10 w-28 rotate-[5deg] rounded-xl object-cover shadow-lg aspect-[3/4]"
                />
                <img
                    src={PRODUCTS[3].src}
                    alt="Moodboard piece"
                    loading="lazy"
                    className="absolute bottom-8 left-1/2 w-20 -translate-x-1/2 rotate-[2deg] rounded-xl object-cover shadow-lg aspect-[3/4]"
                />
                <span className="frosted absolute bottom-3 left-3 rounded-full px-3 py-1.5 text-[9px] font-light tracking-wide text-slate-700">
                    Mood · Quiet luxury
                </span>
            </div>
            <div className="flex items-center justify-center gap-3 py-4">
                {[ImageIcon, Type, Palette, Plus].map((Icon, i) => (
                    <span
                        key={i}
                        className={`flex h-9 w-9 items-center justify-center rounded-full ${
                            i === 0 ? "bg-slate-950 text-white" : "border border-slate-200 text-slate-500"
                        }`}
                    >
                        <Icon size={14} strokeWidth={1.5} />
                    </span>
                ))}
            </div>
        </div>
    );
}

function ClosetScreen() {
    return (
        <div className="flex h-full flex-col pt-9" data-testid="mockup-screen-closet">
            <ScreenHeader title="My Closet" />
            <div className="flex gap-1.5 px-4 pb-3">
                <Chip active>All</Chip>
                <Chip>Outerwear</Chip>
                <Chip>Shoes</Chip>
            </div>
            <div className="grid grid-cols-3 gap-2 px-4">
                {PRODUCTS.slice(0, 6).map((p) => (
                    <img
                        key={p.id}
                        src={p.src}
                        alt={p.name}
                        loading="lazy"
                        className="aspect-square w-full rounded-xl object-cover"
                    />
                ))}
            </div>
            <div className="mt-auto flex justify-center pb-4">
                <span className="rounded-full bg-slate-950 px-5 py-2.5 text-[10px] font-light tracking-wide text-white">
                    Style this look
                </span>
            </div>
        </div>
    );
}

function PhoneFrame({ label, children }) {
    return (
        <div className="flex flex-col items-center">
            <div className="relative w-[236px] rounded-[44px] border border-slate-200 bg-slate-950 p-2.5 shadow-[0_40px_80px_-40px_rgba(15,23,42,0.4)] md:w-[252px]">
                <div className="relative h-[490px] w-full overflow-hidden rounded-[36px] bg-white md:h-[520px]">
                    <div className="absolute left-1/2 top-2.5 z-20 h-[18px] w-20 -translate-x-1/2 rounded-full bg-slate-950" />
                    {children}
                </div>
            </div>
            <p className="mt-6 text-[11px] font-light uppercase tracking-[0.3em] text-slate-400">{label}</p>
        </div>
    );
}

const SCREENS = [
    { label: "Discover", Screen: DiscoverScreen, offset: "md:mt-0" },
    { label: "Shop", Screen: ShopScreen, offset: "md:mt-16" },
    { label: "Studio", Screen: StudioScreen, offset: "md:mt-6" },
    { label: "Closet", Screen: ClosetScreen, offset: "md:mt-24" },
];

export default function AppPreview() {
    return (
        <section id="app-preview" className="px-4 pb-24 sm:px-6 md:pb-36" data-testid="app-preview-section">
            <div className="overflow-hidden rounded-[40px] bg-[#F8F9FA] px-6 py-20 md:rounded-[56px] md:px-12 md:py-28">
                <div className="mx-auto max-w-[1200px]">
                    <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                        <div>
                            <Reveal>
                                <Eyebrow>The app</Eyebrow>
                            </Reveal>
                            <Reveal delay={0.08}>
                                <h2 className="mt-7 text-4xl font-extralight leading-[1.05] tracking-[-0.03em] text-slate-950 sm:text-5xl lg:text-6xl">
                                    Inside <span className="italic font-light text-slate-950">EDIT.</span>
                                </h2>
                            </Reveal>
                        </div>
                        <Reveal delay={0.15}>
                            <p className="max-w-sm text-base font-extralight leading-relaxed text-slate-600">
                                A first look at the Discover, Shop, Studio and Closet experiences.
                            </p>
                        </Reveal>
                    </div>

                    <div className="no-scrollbar -mx-6 mt-16 flex snap-x snap-mandatory gap-8 overflow-x-auto px-6 pb-4 md:mx-0 md:mt-24 md:justify-center md:gap-10 md:overflow-visible md:px-0">
                        {SCREENS.map(({ label, Screen, offset }, i) => (
                            <Reveal key={label} delay={i * 0.12} className={`shrink-0 snap-center ${offset}`}>
                                <PhoneFrame label={label}>
                                    <Screen />
                                </PhoneFrame>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
